/**
 * @license
 * @copyright Copyright Motili Inc., 2024 All Rights Reserved
 */

import Config from 'Config';
import { getOrderSettings } from 'common/services/ClientService';
import { ORDER_STATUS } from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import * as ApiService from 'common/services/ApiService';
import {
    isMotiliAccount,
    isContractorAccount,
    isClientAccount,
    getAccountRoles,
} from 'common/services/AuthService';

const baseUrl = 'orders';

/**
 * create an order
 * @param {object} data
 * @returns {Promise<Object>}
 */
function create(data) {
    return ApiService.post(true, baseUrl, data);
}

/**
 * retrieves an order
 * @param {object} [data]
 * @param {Object} [data.filter]
 * @returns {Promise<Object>}
 */
function find(data) {
    return ApiService.post(true, `${baseUrl}/search`, data);
}

function getVendorActivityCount(orderId) {
    return ApiService.get(true, `${baseUrl}/${orderId}/getVendorActivityCount`);
}

/**
 * retrieves an order
 * @param {number} id
 * @param {object} [query]
 * @returns {Promise<Order>}
 */
function findById(id, query) {
    return ApiService.get(true, `${baseUrl}/${id}`, query);
}

/**
 * retrieves an order by custom filter
 * @param {number} id
 * @param {string} filterId
 * @returns {Promise<Object>}
 */
function fetchOrderByFilterId(id, filterId) {
    return ApiService.get(true, `${baseUrl}/${id}/filter/${filterId}`);
}

/**
 * retrieves dircet supply handling rules for an order
 * @param {number} id
 * @returns {Promise<Object>}
 */
function getOrderRules(id) {
    return ApiService.get(true, `${baseUrl}/${id}/rules`);
}

/**
 * retrieves the number of orders
 * @param {object} [data]
 * @param {Object} [data.where]
 * @returns {Promise<Object>}
 */
function count(data) {
    return ApiService.post(true, `${baseUrl}/search/count`, data);
}

/**
 * create a line item
 * @param {number} id
 * @param {object} lineItem
 * @returns {Promise<Object>}
 */
function createLineItem(id, lineItem) {
    return ApiService.post(true, `${baseUrl}/${id}/lineItems`, lineItem);
}

/**
 * deletes a line item
 * @param {number} orderId
 * @param {number} lineItemId
 * @returns {Promise<Object>}
 */
export function deleteLineItem(orderId, lineItemId) {
    return ApiService.del(`${baseUrl}/${orderId}/lineItems/${lineItemId}`);
}

/**
 * update a line item
 * @param {number} id
 * @param {number} lineItemId
 * @param {object} lineItem
 * @param {boolean} [finance]
 * @returns {Promise<Object>}
 */
export function updateLineItem(id, lineItemId, lineItem, finance) {
    const query = {};
    if (finance) {
        query.finance = finance;
    }
    return ApiService.put(
        true,
        `${baseUrl}/${id}/lineItems/${lineItemId}`,
        lineItem,
        query
    );
}

/**
 * update line item serial numbers
 * @param {number} id
 * @param {object} serialNumberData
 * @returns {Promise<Object>}
 */
export function updateOrderSerialNumbers(id, serialNumberData) {
    return ApiService.patch(
        true,
        `${baseUrl}/${id}/updateOrderSerialNumbers`,
        serialNumberData
    );
}

/**
 * adjust a line item
 * @param {number} id
 * @param {number} lineItemId
 * @param {object} adjustment
 * @param {boolean} [finance]
 * @returns {Promise<Object>}
 */
export function adjustFinanceLineItem(id, lineItemId, adjustment, finance) {
    const query = {};
    if (finance) {
        query.finance = finance;
    }
    return ApiService.post(
        true,
        `${baseUrl}/${id}/lineItems/${lineItemId}/adjustFinance`,
        adjustment,
        query
    );
}

/**
 * bulk adjust line items
 * @param {number} orderId
 * @param {array} bulkAdjustment
 * @param {object} adjustmentReason
 * @param {string} adjustmentReasonNote
 * @returns {Promise}
 */
export function bulkAdjustLineItems(
    orderId,
    bulkAdjustment,
    adjustmentReason,
    adjustmentReasonNote
) {
    const query = {
        bulkAdjustment,
        adjustmentReason,
        adjustmentReasonNote,
    };
    return ApiService.post(
        true,
        `${baseUrl}/${orderId}/lineItems/bulkAdjustment`,
        {},
        query
    );
}

/**
 * approve a line item
 * @param {Number} id
 * @param {Number} lineItemId
 * @param {string} reason
 * @returns {Promise}
 */
export function approveLineItem(id, lineItemId, reason) {
    return ApiService.post(
        true,
        `${baseUrl}/${id}/lineItems/${lineItemId}/approve`,
        {},
        { reason }
    );
}

/**
 * substitute a line item
 * @param {Number} id
 * @param {Number} lineItemId
 * @param {String} newLineItem
 * @param {boolean} isCustom
 * @returns {Promise}
 */
export function substituteLineItem(id, lineItemId, newLineItem, isCustom) {
    const query = {
        isCustom,
    };
    const body = {
        sku: newLineItem.sku,
    };
    if (isCustom) {
        body.suppliedDescription = newLineItem.suppliedDescription;
    }
    return ApiService.post(
        true,
        `${baseUrl}/${id}/lineItems/${lineItemId}/substitute`,
        body,
        query
    );
}

/**
 * change a line item
 * @param {Number} id
 * @param {Number} lineItemId
 * @param {String} newLineItem
 * @param {boolean} isCustom
 * @returns {Promise}
 */
export function changeLineItem(id, lineItemId, newLineItem, isCustom) {
    const query = {
        isCustom,
    };
    const body = {
        sku: newLineItem.sku,
    };
    if (isCustom) {
        body.suppliedDescription = newLineItem.suppliedDescription;
    }
    return ApiService.post(
        true,
        `${baseUrl}/${id}/lineItems/${lineItemId}/change`,
        body,
        query
    );
}

/**
 * patches an order
 * @param {number} id
 * @param {object} patch
 */
function patch(id, patch) {
    // eslint-disable-line no-shadow
    return ApiService.patch(true, `${baseUrl}/${id}`, patch);
}

/**
 * Call remote method Order/update to update FulfillmentDetails and/or LineItems
 * @param {number} id {order Id}
 * @param  {object} data {order update data}
 * @return {Promise<Object>}
 */
function update(id, data) {
    return ApiService.post(true, `${baseUrl}/${id}/updateDocument`, data);
}

/**
 * performs submit action on an order
 * @param {number} id
 * @param {boolean} doneOnBehalfOf
 * @returns {Promise<Object>}
 */
function submit(id, doneOnBehalfOf) {
    // eslint-disable-line no-shadow
    const query = {
        doneOnBehalfOf,
    };
    return ApiService.post(true, `${baseUrl}/${id}/submit`, {}, query);
}

/**
 * performs complete action on an order
 * @param {number} id
 * @param {string} reason
 * @returns {Promise<Object>}
 */
function complete(id, reason) {
    // eslint-disable-line no-shadow
    return ApiService.post(true, `${baseUrl}/${id}/complete`, { reason });
}

/**
 * performs close action on an order
 * @param {number} id
 * @returns {Promise<Object>}
 */
function close(id) {
    // eslint-disable-line no-shadow
    return ApiService.post(true, `${baseUrl}/${id}/close`);
}

/**
 * performs cancelAndClone action on an order
 * @param {number} id
 * @param {string} reason
 * @param {string} reasonId
 * @param {string} secondaryReasonId
 * @returns {Promise<Object>}
 */
function cancelAndClone(id, reason, reasonId, secondaryReasonId) {
    // eslint-disable-line no-shadow
    const query = {
        reason,
        reasonId,
        secondaryReasonId,
    };
    return ApiService.post(true, `${baseUrl}/${id}/cancelClone`, {}, query);
}

/**
 * performs clone action on an order
 * @param {number} id
 * @param {string} reason
 * @returns {Promise<Object>}
 */
function clone(id, reason) {
    // eslint-disable-line no-shadow
    const query = { reason };
    return ApiService.post(true, `${baseUrl}/${id}/clone`, {}, query);
}

/**
 *
 * @param {number} id
 * @param {string} priorityId
 * @param {string} reasonId
 * @param {string} reasonIdLabel
 * @param {string} reason
 * @returns {Promise}
 */
function changePriority(id, priorityId, reasonId, reasonIdLabel, reason) {
    const query = {
        priorityId,
        reasonId,
        reasonIdLabel,
        reason,
    };
    return ApiService.post(true, `${baseUrl}/${id}/changePriority`, {}, query);
}

/**
 * performs order action on an order
 * @param {number} id
 * @param {boolean} override
 * @param {object} adjustedFreight
 * @param {boolean} doneOnBehalfOf
 * @returns {Promise<Object>}
 */
function order(id, override, adjustedFreight, doneOnBehalfOf, advancedFulfill) {
    // eslint-disable-line no-shadow
    const query = {
        override,
        freightAdjustment: adjustedFreight,
        doneOnBehalfOf,
        advancedFulfill,
    };
    return ApiService.post(true, `${baseUrl}/${id}/order`, {}, query);
}

function reOrder(id, adjustedFreight, doneOnBehalfOf) {
    const query = {
        freightAdjustment: adjustedFreight,
        doneOnBehalfOf,
    };
    return ApiService.post(true, `${baseUrl}/${id}/reOrder`, {}, query);
}

function flagClient(id, approved, doneOnBehalfOf, reason) {
    const query = {
        approved,
        doneOnBehalfOf,
        reason,
    };
    return ApiService.post(true, `${baseUrl}/${id}/flagClient`, {}, query);
}

/**
 * performs fulfill action on an order
 * @param {number} id
 * @param {object} options
 * @param {object} adjustedFreight
 * @returns {Promise<Object>}
 */
function fulfill(id, options, adjustedFreight) {
    // eslint-disable-line no-shadow
    const query = {
        orderOptions: options,
        freightAdjustment: adjustedFreight,
    };
    return ApiService.post(true, `${baseUrl}/${id}/fulfill`, {}, query);
}

function ordered(id) {
    return ApiService.post(true, `${baseUrl}/${id}/ordered`);
}

/**
 * performs fulfill action on an order
 * @param {number} id
 * @param {object} options
 * @returns {Promise<Object>}
 */
function placeHold(id, options) {
    return ApiService.post(true, `${baseUrl}/${id}/placeHold`, {}, options);
}

/**
 * performs fulfill action on an order in SUBMITTED status
 * @param {number} id
 * @param {object} options
 * @returns {Promise<Object>}
 */
function placeHoldSubmitted(id, options) {
    // eslint-disable-line no-shadow
    return ApiService.post(
        true,
        `${baseUrl}/${id}/placeHoldSubmitted`,
        {},
        options
    );
}

/**
 * performs confirm action on an order
 * @param {number} id
 * @param {object} optionalOrderObject
 * @returns {Promise<Object>}
 */
function confirm(id, optionalOrderObject) {
    // eslint-disable-line no-shadow
    return ApiService.post(
        true,
        `${baseUrl}/${id}/confirm`,
        optionalOrderObject
    );
}

/**
 * finds the freight rate for an order
 * @param {number} id
 * @returns {Promise<Object>}
 */
function findFreightRate(id) {
    // eslint-disable-line no-shadow
    return ApiService.post(true, `${baseUrl}/${id}/findFreightRate`);
}

/**
 * performs compare action on an order
 * @param {number} id
 * @returns {Promise<Object>}
 */
function compare(id) {
    // eslint-disable-line no-shadow
    return ApiService.post(true, `${baseUrl}/${id}/compare`);
}

/**
 * performs check availability action on an order
 * @param {number} id
 * @returns {Promise<Object>}
 */
function checkAvailability(id) {
    // eslint-disable-line no-shadow
    return ApiService.post(true, `${baseUrl}/${id}/checkAvailablilty`);
}

/**
 * restore makes put request to modify status to restored
 * @param {number} id
 * @param {string} reason
 * @returns {Promise<Object>}
 */
function restore(id, reason) {
    return ApiService.post(true, `${baseUrl}/${id}/restore`, {
        reason,
    });
}

/**
 * performs cancel action on an order
 * @param {number} id
 * @param {string} reason
 * @param {string} reasonId
 * @param {string} secondaryReasonId
 * @param {string | undefined} vendorSystemConfirmationNumber
 * @param {boolean} doneOnBehalfOf
 * @returns {Promise<Object>}
 */
function cancel(
    id,
    reason,
    reasonId,
    secondaryReasonId,
    vendorSystemConfirmationNumber,
    doneOnBehalfOf
) {
    const query = {
        reason,
        reasonId,
        secondaryReasonId,
        vendorSystemConfirmationNumber,
        doneOnBehalfOf,
    };
    return ApiService.post(true, `${baseUrl}/${id}/cancel`, {}, query);
}

export function generatePdf(
    id,
    fileName,
    statementOfWork,
    selectedLineItemIds,
    hidePricing,
    hideBundleItems,
    hideSerialNumbers
) {
    const query = {
        fileName,
        statementOfWork,
        selectedLineItemIds,
        hidePricing,
        hideBundleItems,
        hideSerialNumbers,
    };
    return ApiService.post(true, `${baseUrl}/${id}/export`, {}, query);
}

/**
 * performs export action on an order
 * PDF passed as exportTypeId argument
 * @param {number} id
 * @returns {Promise<Object>}
 */
function exportPdf(id) {
    // eslint-disable-line no-shadow
    const query = {
        exportTypeId: 'PDF',
    };
    return ApiService.post(
        true,
        `${baseUrl}/${id}/export`,
        { download: true },
        query
    );
}

/**
 * performs export action on an order
 * PDF passed as exportTypeId argument
 * @param {object} filter
 * @returns {Promise<Object>}
 */
function exportCsv(filter) {
    const query = {
        exportTypeId: 'CSV',
        filter,
    };
    return ApiService.post(
        true,
        `${baseUrl}/export`,
        { download: true },
        query
    );
}

/**
 * Create a new note for the order
 * @param {*} orderId
 * @param {*} data
 * @param {*} addToComment
 * @param {*} editable
 * @returns {Promise}
 */
export function createNote(orderId, data, addToComment, editable) {
    return ApiService.post(true, `${baseUrl}/${orderId}/notes`, data, {
        addToComment,
        editable,
    });
}

/**
 * Update a note for the order
 * @param {*} orderId
 * @param {*} noteId
 * @param {*} data
 * @returns {Promise}
 */
export function updateNote(orderId, noteId, data) {
    return ApiService.put(true, `${baseUrl}/${orderId}/notes/${noteId}`, data);
}

/**
 * Create a new note for the order
 * @param {*} orderId
 * @param {*} data
 * @returns {Promise}
 */
export function createComment(orderId, data) {
    return ApiService.post(true, `${baseUrl}/${orderId}/comments`, data);
}

/**
 * Update a note for the order
 * @param {*} orderId
 * @param {*} commentId
 * @param {*} data
 * @returns {Promise}
 */
export function updateComment(orderId, commentId, data) {
    return ApiService.put(
        true,
        `${baseUrl}/${orderId}/comments/${commentId}`,
        data
    );
}

export function createContact(orderId, data) {
    return ApiService.post(true, `${baseUrl}/${orderId}/contacts`, data);
}

export function removeContact(orderId, contactId) {
    return ApiService.del(`${baseUrl}/${orderId}/contacts/${contactId}`);
}

/**
 * maps truck store fulfillment details to order object
 * @param {Object} _order
 * @param {Object} fulfillmentDetails
 * @param {String} fulfillmentDetails.fulfillmentToAddress
 * @param {String} fulfillmentDetails.fulfillmentToCity
 * @param {String} fulfillmentDetails.fulfillmentToRegion
 * @param {String} fulfillmentDetails.fulfillmentToPostalCode
 * @param {String} fulfillmentDetails.fulfillmentToStdCounty
 * @param {String} fulfillmentDetails.fulfillmentToStdCountry
 * @param {String} fulfillmentDetails.fulfillmentTypeId
 * @param {String} fulfillmentDetails.fulfillmentTo
 * @param {Number} fulfillmentDetails.distributionCenterId
 * @param {Boolean} fulfillmentDetails.residentialDelivery
 * @returns {Object} order
 */
export function mapFulfillmentDetailsFromTruck(
    _order = {},
    fulfillmentDetails = {}
) {
    return {
        ..._order,
        fulfillmentToAddress: fulfillmentDetails.fulfillmentToAddress,
        fulfillmentToStdAddress: fulfillmentDetails.fulfillmentToAddress,
        fulfillmentToCity: fulfillmentDetails.fulfillmentToCity,
        fulfillmentToStdCity: fulfillmentDetails.fulfillmentToCity,
        fulfillmentToRegion: fulfillmentDetails.fulfillmentToRegion,
        fulfillmentToStdRegion: fulfillmentDetails.fulfillmentToRegion,
        fulfillmentToPostalCode: fulfillmentDetails.fulfillmentToPostalCode,
        fulfillmentToStdPostalCode: fulfillmentDetails.fulfillmentToPostalCode,
        fulfillmentToStdCounty: fulfillmentDetails.fulfillmentToStdCounty,
        fulfillmentToStdCountry:
            fulfillmentDetails.fulfillmentToStdCountry ||
            fulfillmentDetails.fulfillmentToCountry,
        fulfillmentTypeId: fulfillmentDetails.fulfillmentTypeId,
        fulfillmentTo: fulfillmentDetails.fulfillmentTo,
        fulfillmentDistributionCenterId:
            fulfillmentDetails.distributionCenterId,
        residentialDelivery: fulfillmentDetails.residentialDelivery,
    };
}

export function orderLocked(status) {
    if (!status) {
        console.error(
            new Error(
                'OrderService#orderLocked invoked without status argument'
            )
        );
        return false;
    }
    const roles = getAccountRoles();
    if (isMotiliAccount()) {
        for (let i = 0; i < roles.length; i += 1) {
            const role = roles[i];
            if (role && status.locked.motili[role]) {
                return true;
            }
        }
        return false;
    }
    if (isContractorAccount()) {
        for (let i = 0; i < roles.length; i += 1) {
            const role = roles[i];
            if (role && status.locked.contractor[role]) {
                return true;
            }
        }
        return false;
    }
    if (isClientAccount()) {
        for (let i = 0; i < roles.length; i += 1) {
            const role = roles[i];
            if (role && status.locked.client[role]) {
                return true;
            }
        }
        return false;
    }
    return false;
}

function updateReferenceNumber(orderId, referenceToUpdate) {
    return ApiService.post(
        true,
        `${baseUrl}/${orderId}/updateReference`,
        referenceToUpdate
    );
}

function getOrderLogisticsTypes() {
    return ApiService.get(true, 'Orders/logisticsTypes');
}

function changeProject(orderId, projectId) {
    return ApiService.patch(
        true,
        `${baseUrl}/${orderId}/changeProject`,
        {},
        { projectId }
    );
}

function specifyOrderIssue(orderId, data) {
    return ApiService.patch(true, `${baseUrl}/${orderId}/specifyIssue`, data);
}

function solveOrderIssue(orderId, issueId) {
    return ApiService.patch(
        true,
        `${baseUrl}/${orderId}/solveIssue`,
        {},
        { issueId }
    );
}

function transferOwner(orderId, ownerId, coOwnerId) {
    const query = { ownerId, ...(coOwnerId && { coOwnerId }) };
    return ApiService.post(
        true,
        `${baseUrl}/${orderId}/transferOwner`,
        {},
        query
    );
}

function createReturnOrder(orderId, returnOrder) {
    return ApiService.post(true, `Orders/${orderId}/orderReturns`, returnOrder);
}

function updateReturnOrder(orderId, returnOrderId, returnOrder) {
    return ApiService.patch(
        true,
        `Orders/${orderId}/orderReturns/${returnOrderId}`,
        returnOrder
    );
}

function cancelReturnOrder(orderId, returnOrderId, returnOrder) {
    return ApiService.patch(
        true,
        `Orders/${orderId}/orderReturns/${returnOrderId}/cancel`,
        returnOrder
    );
}

function calculateLineItemCost(id, data) {
    return ApiService.post(
        true,
        `${baseUrl}/${id}/lineItems/calculateLineItemCost`,
        data
    );
}
function isLiftGateRequired(order) {
    if (!order || !order.lineItems || order.lineItems.length === 0) {
        return false;
    }
    const liftGateLineitem = order.lineItems.find(
        a => a.sku === Config.liftGateSku
    );
    if (liftGateLineitem) {
        return true;
    }
    return false;
}

function updateBillTo(orderId, billTo) {
    return ApiService.patch(true, `${baseUrl}/${orderId}/updateBillTo`, billTo);
}

async function updateFulfillmentMethodIfValid(
    orderId,
    orderFulfillmentMethod,
    truckFulfillmentTypeId
) {
    if (
        orderFulfillmentMethod &&
        orderFulfillmentMethod.fulfillmentTypeId === truckFulfillmentTypeId
    ) {
        await patch(orderId, {
            fulfillmentMethod: orderFulfillmentMethod.id,
        });
    }
}

export async function getClientOrderSettings(clientId) {
    let hasFulfillmentMethods;
    let propertyRequired;
    let enableDisplayAvailableStockOption;
    let enableDisplayAHRIFilter;
    if (clientId) {
        const orderSettings = await getOrderSettings(clientId);
        if (orderSettings) {
            if (isMotiliAccount() || isClientAccount()) {
                // Only Admin and Client accounts interact with fulfillment methods
                hasFulfillmentMethods = orderSettings.enableOrderTypeDisplay;
            }
            propertyRequired = orderSettings.enableRequiredProperty;
            enableDisplayAvailableStockOption =
                orderSettings.enableDisplayAvailableStockOption;
            enableDisplayAHRIFilter = orderSettings.enableDisplayAHRIFilter;
        }
    }
    return {
        hasFulfillmentMethods,
        propertyRequired,
        enableDisplayAvailableStockOption,
        enableDisplayAHRIFilter,
    };
}

function getWarrantyOwner(orderId) {
    return ApiService.get(true, `${baseUrl}/${orderId}/warrantyOwner`);
}

async function getShippingToForOrders(searchTerm, clientId) {
    return ApiService.get(true, `${baseUrl}/getShippingToForOrders`, {
        searchTerm,
        clientId,
    });
}

async function getPastReturnTax(orderId, returnId) {
    return ApiService.get(
        true,
        `${baseUrl}/${orderId}/getPastReturnTax/${returnId}`
    );
}

async function calculateTaxWithDocument(orderId, document) {
    return ApiService.post(
        true,
        `${baseUrl}/${orderId}/calculateTaxWithDocument`,
        document
    );
}

async function countActiveContractorInvoicedOrdersByContractorId(
    contractorPaymentAccountId
) {
    const filter = {
        where: {
            statusId: { nin: [ORDER_STATUS.CANCELLED, ORDER_STATUS.CLOSED] },
            payeeClientId: contractorPaymentAccountId,
        },
    };
    return count(filter);
}
/**
 * get document timestamps
 * @param {number} id
 * @returns {Promise<Object>}
 */
function getDocumentTimestamps(id) {
    return ApiService.get(true, `${baseUrl}/${id}/documentTimestamps`);
}

/**
 * retrieves all order related job notes
 * @param {number} orderId
 * @returns {Promise<Array>}
 */
function getOrderRelatedJobNotes(orderId) {
    return ApiService.get(true, `Orders/${orderId}/documentNotes`);
}

/**
 * clones an order
 * @param {number} orderId
 * @param {String} reason
 * @param {Object} updates
 * @returns {Promise<{}>}
 */
function cloneOrder(orderId, reason, updates) {
    return ApiService.post(
        true,
        `${baseUrl}/${orderId}/cloneAnyOrder`,
        updates,
        { reason }
    );
}

export {
    create,
    find,
    findById,
    fetchOrderByFilterId,
    count,
    patch,
    createLineItem,
    submit,
    complete,
    close,
    cancelAndClone,
    changePriority,
    order,
    reOrder,
    flagClient,
    fulfill,
    ordered,
    placeHold,
    placeHoldSubmitted,
    confirm,
    findFreightRate,
    compare,
    restore,
    cancel,
    exportPdf,
    exportCsv,
    checkAvailability,
    updateReferenceNumber,
    getOrderLogisticsTypes,
    changeProject,
    update,
    clone,
    createReturnOrder,
    updateReturnOrder,
    cancelReturnOrder,
    getOrderRules,
    specifyOrderIssue,
    solveOrderIssue,
    getVendorActivityCount,
    calculateLineItemCost,
    transferOwner,
    isLiftGateRequired,
    updateBillTo,
    updateFulfillmentMethodIfValid,
    getWarrantyOwner,
    getShippingToForOrders,
    getPastReturnTax,
    calculateTaxWithDocument,
    countActiveContractorInvoicedOrdersByContractorId,
    getDocumentTimestamps,
    getOrderRelatedJobNotes,
    cloneOrder,
};
